import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Attendant } from '@/interfaces/attendant'
import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { IconArrowDown, IconArrowUp, IconTrash } from '@tabler/icons-react'
import { Row } from '@tanstack/react-table'
import { useAttendants } from '../context/attendants-context'

import { listAttendantsQuery } from '@/queries/attendant.query'
import { AttendantService } from '@/services/attendant.service'
import { useAuthStore } from '@/stores/auth.store'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

interface DataTableRowActionsProps<TData> {
  row: Row<TData>
}

export function DataTableRowActions<TData>({
  row,
}: Readonly<DataTableRowActionsProps<TData>>) {
  const attendant = row.original as Attendant
  const { setOpen, setCurrentRow } = useAttendants()
  const queryClient = useQueryClient()
  const getHasFullTeamAccess = useAuthStore(
    (state) => state.getHasFullTeamAccess
  )
  const canManageAttendants = getHasFullTeamAccess()

  const { mutateAsync: promoteAttendant } = useMutation({
    mutationFn: AttendantService.promote,
    onSuccess: () => {
      toast.success('Atendente promovido com sucesso!')
      queryClient.invalidateQueries({ queryKey: listAttendantsQuery.queryKey })
    },
    onError: () => {
      toast.error('Erro ao promover atendente')
    }
  })

  const { mutateAsync: demoteAttendant } = useMutation({
    mutationFn: AttendantService.demote,
    onSuccess: () => {
      toast.success('Gerente rebaixado com sucesso!')
      queryClient.invalidateQueries({ queryKey: listAttendantsQuery.queryKey })
    },
    onError: () => {
      toast.error('Erro ao rebaixar gerente')
    }
  })

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
        >
          <DotsHorizontalIcon className='h-4 w-4' />
          <span className='sr-only'>Abrir menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-[160px]'>
        <DropdownMenuItem
          disabled={!canManageAttendants}
          onClick={() => {
            if (!canManageAttendants) return
            setCurrentRow(attendant)
            setOpen('update')
          }}
        >
          Editar
        </DropdownMenuItem>
        {attendant.role === 'ATTENDANT' && (
          <DropdownMenuItem
            disabled={!canManageAttendants}
            onClick={() => {
              if (!canManageAttendants) return
              promoteAttendant(attendant.id)
            }}
          >
            Promover
            <DropdownMenuShortcut>
              <IconArrowUp size={16} />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        )}
        {attendant.role === 'MANAGER' && (
          <DropdownMenuItem
            disabled={!canManageAttendants}
            onClick={() => {
              if (!canManageAttendants) return
              demoteAttendant(attendant.id)
            }}
          >
            Rebaixar
            <DropdownMenuShortcut>
              <IconArrowDown size={16} />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!canManageAttendants}
          onClick={() => {
            if (!canManageAttendants) return
            setCurrentRow(attendant)
            setOpen('delete')
          }}
        >
          Excluir
          <DropdownMenuShortcut>
            <IconTrash size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
